<template>
  <div class="bookingsCalendarContainer">
    <FullCalendar ref="calendar" :options="calendarOptions" />
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import FullCalendar from "@fullcalendar/vue";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import dayGridPlugin from "@fullcalendar/daygrid";
import ColorHash from "color-hash";
import * as Time from "@/helpers/time";
import {
  monthAnchorDateFromView,
  sameCalendarMonth,
} from "@/helpers/calendarMonthAnchor";
import { bookingStatusTypes, paymentStatusTypes } from "@/store/bookings";
import { get } from "lodash";

const SLOT_DURATION_MINUTES = 15;

const colorHash = new ColorHash();

export default {
  components: {
    FullCalendar,
  },
  props: {
    date: String,
    bookings: Array,
  },
  data() {
    return {
      calendarOptions: {
        plugins: [
          momentTimezonePlugin,
          resourceTimeGridPlugin,
          dayGridPlugin,
          interactionPlugin,
        ],
        timeZone: "GMT",
        schedulerLicenseKey: "CC-Attribution-NonCommercial-NoDerivatives",
        customButtons: {
          addBooking: {
            text: "Book Now",
            click: this.handleAddBookingClick,
          },searchBookings: {
            text: "Search",
            click: this.handleSearchBookingClick,
          }
        },
        headerToolbar: {
          start: "prev,next today",
          center: "title",
          end: "resourceTimeGridDay,dayGridMonth,addBooking,searchBookings",
        },
        views: {
          resourceTimeGridDay: {
            titleFormat: {
              weekday: "short",
              month: "short",
              day: "numeric",
            },
          },
          dayGridMonth: {
            titleFormat: {
              month: "short",
              year: "numeric",
            },
            dayMaxEventRows: 0,
            moreLinkContent(info) {
              if (!info.num) return "";
              if (info.num === 1) return `1 booking`;
              return `${info.num} bookings`;
            },
            moreLinkClick: this.handleDateClick,
          },
        },
        initialView: "resourceTimeGridDay",
        initialDate: this.date,
        now: moment().utc(true).format("YYYY-MM-DDTHH:mm:ss"),
        nowIndicator: true,
        scrollTime: moment().utc(true).format("HH:mm"),
        allDaySlot: false,
        filterResourcesWithEvents: true,
        events: [],
        resources: [],
        resourceOrder: "title",
        height: "100%",
        slotDuration: `00:${SLOT_DURATION_MINUTES}`,
        slotLabelInterval: "01:00",
        businessHours: {},
        // event titles don't implicitly support HTML,
        // so we use the event content hook to reinstate
        // the event title as HTML.
        eventContent({ event }) {
          return {
            html: event.title,
          };
        },
        eventClick: this.handleEventClick,
        dateClick: this.handleDateClick,
        datesSet: this.handleDatesSet,
        viewDidMount: this.handleViewDidMount,
      },
    };
  },
  methods: {
    ...mapActions({
      fetchServices: "services/fetchServices",
      fetchEmployees: "employees/fetchEmployees",
    }),
    getEventTitle(event) {
      const service = this.services[event.serviceId];
      const employee = this.employees[event.resourceId];
      const serviceName = service
        ? service.name
        : "(removed service)";
      const employeeName = employee
        ? employee.name
        : "(removed employee)";
      const servicePackage =
        service && service.packages
          ? service.packages[event.servicePackageId]
          : null;
      const startTime = Time.timestampToTimeReadable(event.start);
      const endTime = Time.timestampToTimeReadable(event.end);
      const timeHTML = `<strong>${startTime} - ${endTime}</strong>`;
      const paymentStatusText = this.$t(
        `message.bookingPaymentStatusTypes.${event.paymentStatus}`
      );
      const paymentHTML = `€${event.total} ${paymentStatusText}`;
      const serviceHTML = `${get(event, "customer.name", "Unknown")} - ${get(
        servicePackage,
        "name",
        "Unnamed"
      )} ${serviceName} · ${employeeName}`;
      const duration = moment.duration(
        moment(event.end).diff(moment(event.start))
      );
      const durationAsMinutes = duration.asMinutes();

      if (durationAsMinutes < SLOT_DURATION_MINUTES * 2) {
        return `${timeHTML} | ${serviceHTML} - ${paymentHTML}`;
      }

      if (durationAsMinutes < SLOT_DURATION_MINUTES * 3) {
        return `${timeHTML}<br/>${serviceHTML} - ${paymentHTML}`;
      }

      return `${timeHTML}<br/>${serviceHTML}<br/>${paymentHTML}`;
    },
    getEventBackgroundColor({ bookingStatus, paymentStatus, resourceId }) {
      // TODO const the colours
      if (bookingStatus === bookingStatusTypes.CANCELLED) return "grey";
      if (paymentStatus === paymentStatusTypes.NOT_PAID) return "red";
      return colorHash.hex(resourceId);
    },
    getEvent(booking) {
      const { _id, resourceId, start, end } = booking;
      const title = this.getEventTitle(booking);
      const backgroundColor = this.getEventBackgroundColor(booking);

      return {
        id: _id,
        // FullCalendar resourceTimeGrid expects a string resourceId
        resourceId: resourceId,
        start,
        end,
        title: title || "(booking)",
        backgroundColor,
        borderColor: "transparent",
      };
    },
    handleEventClick({ event }) {
      const { id } = event;
      const booking = this.bookings.find((item) => item._id === id);
      this.$emit("booking-click", booking);
    },
    handleDateChange(dateObj) {
      const date = moment(dateObj).tz("GMT").format(Time.DATE_FORMAT_CODE);
      if (date === this.date) return;
      this.$emit("date-change", date);
    },
    handleDateClick(dateClickInfo) {
      this.handleDateChange(dateClickInfo.date);
      this.calendarApi.changeView("resourceTimeGridDay");
    },
    handleDatesSet({ start, view }) {
      const viewType =
        (view && view.type) ||
        (this.calendarApi && this.calendarApi.view && this.calendarApi.view.type);
      if (viewType === "resourceTimeGridDay") {
        this.handleDateChange(start);
      } else if (viewType === "dayGridMonth") {
        // currentStart = 1st of displayed month (not grid padding from prev month)
        const monthDate = monthAnchorDateFromView(
          view,
          start,
          Time.DATE_FORMAT_CODE
        );
        if (!sameCalendarMonth(monthDate, this.date, Time.DATE_FORMAT_CODE)) {
          this.$emit("date-change", monthDate);
        }
      }
    },
    handleViewDidMount({ view }) {
      this.$emit("view-change", view.type);
    },
    handleAddBookingClick() {
      if (this.calendarApi.view.type === "resourceTimeGridDay") {
        this.$router.push(`/bookings/add?date=${this.date}`);
      } else {
        this.$router.push("/bookings/add");
      }
    },
    handleSearchBookingClick() {
      this.$router.push("/bookings/search");
    }
  },
  computed: {
    ...mapGetters({
      services: "services/services",
      employees: "employees/employees",
      employeesList: "employees/employeesList",
      startTime: "shops/shopEarliestOpeningTime",
      endTime: "shops/shopLatestClosingTime",
    }),
    dateReadable() {
      return Time.formatDateReadable(this.date);
    },
    resources() {
      return this.employeesList.map((employee) => ({
        id: employee._id,
        title: employee.name,
      }));
    },
    events() {
      return this.bookings.map(this.getEvent);
    },
    calendarApi() {
      return this.$refs.calendar.getApi();
    },
  },
  async created() {
    await this.fetchServices();
    await this.fetchEmployees();
  },
  watch: {
    date(value) {
      if (!this.$refs.calendar || !value) return;
      const api = this.calendarApi;
      if (!api) return;
      // Avoid re-entry: gotoDate re-fires datesSet; only move when needed
      const current = api.getDate && api.getDate();
      if (current) {
        const currentStr = moment(current)
          .tz("GMT")
          .format(Time.DATE_FORMAT_CODE);
        if (currentStr === value) return;
        if (
          api.view &&
          api.view.type === "dayGridMonth" &&
          sameCalendarMonth(currentStr, value, Time.DATE_FORMAT_CODE)
        ) {
          return;
        }
      }
      api.gotoDate(value);
    },
    resources(value) {
      this.calendarOptions.resources = value;
    },
    events(value) {
      this.calendarOptions.events = value;
    },
    startTime(value) {
      if (!value) return;
      this.calendarOptions.businessHours.startTime = value;
    },
    endTime(value) {
      if (!value) return;
      this.calendarOptions.businessHours.endTime = value;
    },
  },
};
</script>
<style lang="scss">
.bookingsCalendarContainer {
  height: 80vh;

  @media screen and (max-width: 767px) {
    .fc .fc-toolbar.fc-header-toolbar {
      flex-direction: column;

      .fc-toolbar-chunk {
        padding: 0.5em;
      }
    }
  }
}
</style>
